import type { VerifyResponse } from '@dynamic-labs/sdk-api-core';

import { getCore } from '../../../../../client/core/getCore';
import { getDefaultClient } from '../../../../../client/defaultClient';
import { createApiClient } from '../../../../apiClient';
import { getPrimaryWalletAccount } from '../../../../wallets/primaryWalletAccount/getPrimaryWalletAccount';
import { updateAuthFromVerifyResponse } from '../../../updateAuthFromVerifyResponse';

type UnlinkSocialAccountParams = {
  verifiedCredentialId: string;
};

/**
 * Unlinks a social account from the user's account.
 *
 * @param params.verifiedCredentialId - The verified credential ID of the social account to unlink.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the verify response.
 */
export const unlinkSocialAccount = async (
  { verifiedCredentialId }: UnlinkSocialAccountParams,
  client = getDefaultClient()
): Promise<VerifyResponse> => {
  const core = getCore(client);
  const apiClient = createApiClient({}, client);

  // the api seems to require us to pass the primary wallet id when
  // unlinking a social account if the user has wallets
  const primaryWalletAccount = getPrimaryWalletAccount(client);
  const walletId = primaryWalletAccount?.verifiedCredentialId?.replace(
    '-zerodev',
    ''
  );

  const response = await apiClient.verifyUnlink({
    environmentId: core.environmentId,
    verifyUnlinkRequest: {
      verifiedCredentialId,
      walletId: walletId ?? undefined,
    },
  });

  updateAuthFromVerifyResponse({ response }, client);

  return response;
};
